import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, CardBody, CardFooter } from 'reactstrap';
import { OAuthApp, OAuthAuthorization } from '../../../APIClient';
import { Loading, MaterialIcon, UserStateComponent } from '../../../components';
import { ActionType } from '../../../services';
import { useTranslation } from '../../../translations';
import { Fetcher, isLogicError, isResourceError, useFetcher, useUserSafe } from '../../../utils';
import { captureError, LogicError } from '../../../utils/errorHandling';
import { Authorized, Denied, OAuthPermission, OAuthState } from './components';
import { OAuthContainer } from './components/Container';

export const OAuthAuthorize: FC = () => {
  const { ts } = useTranslation();
  const location = useLocation();

  const fetch = useFetcher(async (): Promise<OAuthApp> => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client_id');
    const responseType = params.get('response_type');
    const scope = params.get('scope');
    const redirectUri = params.get('redirect_uri');

    const missing_fields = [] as string[];
    if (!clientId) {
      missing_fields.push('client_id');
    }

    if (!responseType) {
      missing_fields.push('response_type');
    }

    if (!scope) {
      missing_fields.push('scope');
    }

    if (!redirectUri) {
      missing_fields.push('redirect_uri');
    }

    if (missing_fields.length > 0) {
      throw new Error(`Missing fields: ${missing_fields.join(', ')}`);
    }

    try {
      return await api.getOAuthApplicationAuthorizationInfo(clientId!, scope!, redirectUri!);
    } catch (error) {
      if (
        isLogicError(error, LogicError.OAuthInvalidScope) ||
        isLogicError(error, LogicError.UserMissingPermissions)
      ) {
        // Get the logic errors and throw them to display them to the user
        throw new Error(
          error.apiResponse.errors.logic.reduce(
            (acc, curr) => `${acc}${acc.length > 0 ? ',' : ''} ${curr.message}`,
            '',
          ),
        );
      }

      if (isResourceError(error, 'OAuthApplication')) {
        throw new Error('Invalid or disabled client_id');
      }

      throw error;
    }
  }, [location.search]);

  return (
    <UserStateComponent>
      {fetch.data ? (
        <OAuthAppInfo app={fetch.data} />
      ) : fetch.error ? (
        <CardBody className="text-center">
          <MaterialIcon className="override" large name="error" type="danger" />
          <h4>{ts('invalid_request')}</h4>
          <div>{ts('this_authorization_request_is_malformed_or_the_application_has_been')}</div>
          <div className="text-muted mt-2">
            <small>{fetch.error.message}</small>
          </div>
        </CardBody>
      ) : (
        <Fetcher inline result={fetch} />
      )}
    </UserStateComponent>
  );
};

interface OAuthAppInfoProps {
  readonly app: OAuthApp;
}

const OAuthAppInfo: FC<OAuthAppInfoProps> = ({ app }) => {
  const { ts } = useTranslation();
  const { name, scopeInfo, userAlreadyAuthorized } = app;
  const [authToken, setAuthToken] = useState<OAuthAuthorization | null>(null);
  const [authStatus, setAuthStatus] = useState<OAuthState>(
    userAlreadyAuthorized ? OAuthState.PriorAuthorization : OAuthState.Pending,
  );

  const location = useLocation();
  const dispatch = useDispatch();
  const user = useUserSafe();
  const hasElevatedPermission = scopeInfo?.some((scope) => scope.name.startsWith('admin:'));

  const authorizeApp = useCallback(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const responseType = params.get('response_type');
      const scope = params.get('scope');

      const token = await api.authorizeOAuthApplication(
        app.clientId,
        params.get('redirect_uri')!,
        responseType!,
        scope!,
      );

      setAuthToken(token);
      setAuthStatus(OAuthState.Authorized);
    } catch (error) {
      captureError(error as Error);
    }
  }, [app.clientId, location.search]);

  const denyApp = useCallback(() => {
    setAuthStatus(OAuthState.Denied);
  }, []);

  useEffect(() => {
    dispatch({ type: ActionType.OnlyShowBody });

    if (app.userAlreadyAuthorized) {
      authorizeApp().catch((error) => captureError(error as Error));
    }
  }, []);

  if (authStatus === OAuthState.Authorized) {
    return <Authorized app={app} authorization={authToken!} />;
  }

  if (authStatus === OAuthState.Denied) {
    return <Denied app={app} />;
  }

  if (authStatus === OAuthState.PriorAuthorization) {
    return <Loading inline />;
  }

  return (
    <OAuthContainer app={app}>
      <CardBody className="oauthHeader">
        <div className="oauth-access-info">
          <h3>{name}</h3>
          <span>
            wants to access your account, <strong>{user.username}</strong>, and needs your
            permission to:
          </span>
        </div>
      </CardBody>
      <CardBody>
        <div className="permission">
          <MaterialIcon medium name="vpn_key" />
          <div className="permission-info">
            <div className="permissionHeader">{ts('sign_in_automatically')}</div>
            <div>{ts('signing_in_with_your_concat_account_will_automatically_sign_you')}.</div>
          </div>
        </div>
        {(scopeInfo ?? []).map((scope) => (
          <OAuthPermission key={scope.name} scope={scope} />
        ))}
      </CardBody>
      <CardFooter>
        <div>
          <small>{ts('accepting_these_permissions_means')}</small>
        </div>
        <br />
        <Button block color={hasElevatedPermission ? 'danger' : 'success'} onClick={authorizeApp}>
          {ts('authorize_application')}
        </Button>
        <Button block color="secondary" onClick={denyApp} outline>
          {ts('common.cancel')}
        </Button>
      </CardFooter>
    </OAuthContainer>
  );
};
